// Storage utilities for persisting data - web implementation using localStorage

class StorageService {
  constructor() {
    this.STORAGE_KEYS = {
      SETTINGS: 'bible_study_settings',
      STUDY_GUIDES: 'bible_study_guides',
      RECENT_FILES: 'bible_study_recent_files'
    };
  }

  async loadSettings(userId = 'default_user') {
    if (window.electronAPI) {
      return await window.electronAPI.loadSettings();
    }
    // Web implementation using localStorage
    try {
      const settings = localStorage.getItem(this.STORAGE_KEYS.SETTINGS);
      if (settings) {
        return JSON.parse(settings);
      }
      // Default settings for new users
      const defaultSettings = {
        theme: 'default',
        fontSize: 'medium',
        defaultTranslation: 'KJV',
        autoSave: true,
        showVerseNumbers: true,
        highlightKeywords: true,
        exportFormat: 'pdf'
      };
      await this.saveSettings(defaultSettings);
      return defaultSettings;
    } catch (error) {
      console.error('Error loading settings:', error);
      return {
        theme: 'default',
        fontSize: 'medium',
        defaultTranslation: 'KJV',
        autoSave: true,
        showVerseNumbers: true,
        highlightKeywords: true,
        exportFormat: 'pdf'
      };
    }
  }

  async saveSettings(settings, userId = 'default_user') {
    if (window.electronAPI) {
      return await window.electronAPI.saveSettings(settings);
    }
    // Web implementation using localStorage
    try {
      localStorage.setItem(this.STORAGE_KEYS.SETTINGS, JSON.stringify(settings));
      return { success: true };
    } catch (error) {
      console.error('Error saving settings:', error);
      return { success: false, error: error.message };
    }
  }

  async saveStudyGuide(guide) {
    if (window.electronAPI) {
      try {
        const result = await window.electronAPI.saveFile(guide);
        return result;
      } catch (error) {
        console.error('Error saving study guide:', error);
        return { success: false, error: error.message };
      }
    }
    // Web implementation using localStorage
    try {
      const guides = await this.listStudyGuides();
      const existingIndex = guides.findIndex(g => g.id === guide.id);

      if (existingIndex >= 0) {
        guides[existingIndex] = { ...guide, updatedAt: new Date().toISOString() };
      } else {
        guides.push({ ...guide, id: guide.id || this.generateId(), createdAt: new Date().toISOString() });
      }

      localStorage.setItem(this.STORAGE_KEYS.STUDY_GUIDES, JSON.stringify(guides));
      await this.addToRecentFiles(guide.id || guides[guides.length - 1].id);
      return { success: true, id: guide.id || guides[guides.length - 1].id };
    } catch (error) {
      console.error('Error saving study guide:', error);
      return { success: false, error: error.message };
    }
  }

  async loadStudyGuide(id) {
    if (window.electronAPI) {
      return await window.electronAPI.loadFile();
    }
    // Web implementation using localStorage
    try {
      const guides = await this.listStudyGuides();
      return guides.find(g => g.id === id) || null;
    } catch (error) {
      console.error('Error loading study guide:', error);
      return null;
    }
  }

  async listStudyGuides() {
    if (window.electronAPI) {
      // For Electron, we don't have a list function, so return empty
      return [];
    }
    // Web implementation using localStorage
    try {
      const guides = localStorage.getItem(this.STORAGE_KEYS.STUDY_GUIDES);
      return guides ? JSON.parse(guides) : [];
    } catch (error) {
      console.error('Error listing study guides:', error);
      return [];
    }
  }

  async deleteStudyGuide(id) {
    if (window.electronAPI) {
      return false; // Not implemented for Electron file-based storage
    }
    // Web implementation using localStorage
    try {
      const guides = await this.listStudyGuides();
      const filteredGuides = guides.filter(g => g.id !== id);
      localStorage.setItem(this.STORAGE_KEYS.STUDY_GUIDES, JSON.stringify(filteredGuides));
      return { success: true };
    } catch (error) {
      console.error('Error deleting study guide:', error);
      return { success: false, error: error.message };
    }
  }

  async getRecentFiles(userId = 'default_user', limit = 5) {
    if (window.electronAPI) {
      return []; // Not implemented for Electron
    }
    // Web implementation using localStorage
    try {
      const recentFiles = localStorage.getItem(this.STORAGE_KEYS.RECENT_FILES);
      if (recentFiles) {
        const files = JSON.parse(recentFiles);
        return files.slice(0, limit);
      }
      return [];
    } catch (error) {
      console.error('Error getting recent files:', error);
      return [];
    }
  }

  async addToRecentFiles(guideId, userId = 'default_user') {
    if (window.electronAPI) {
      return; // Not implemented for Electron
    }
    // Web implementation using localStorage
    try {
      const recentFiles = await this.getRecentFiles(userId, 10); // Get more to filter
      const filteredFiles = recentFiles.filter(id => id !== guideId);
      filteredFiles.unshift(guideId);

      localStorage.setItem(this.STORAGE_KEYS.RECENT_FILES, JSON.stringify(filteredFiles.slice(0, 10)));
    } catch (error) {
      console.error('Error adding to recent files:', error);
    }
  }

  async searchStudyGuides(query, category = null, tags = []) {
    if (window.electronAPI) {
      return []; // Not implemented for Electron
    }
    // Web implementation using localStorage
    try {
      const guides = await this.listStudyGuides();
      let filteredGuides = guides;

      if (query) {
        const searchTerm = query.toLowerCase();
        filteredGuides = filteredGuides.filter(guide =>
          guide.title?.toLowerCase().includes(searchTerm) ||
          guide.content?.toLowerCase().includes(searchTerm) ||
          guide.description?.toLowerCase().includes(searchTerm)
        );
      }

      if (category) {
        filteredGuides = filteredGuides.filter(guide => guide.category === category);
      }

      if (tags && tags.length > 0) {
        filteredGuides = filteredGuides.filter(guide =>
          guide.tags?.some(tag => tags.includes(tag))
        );
      }

      return filteredGuides;
    } catch (error) {
      console.error('Error searching study guides:', error);
      return [];
    }
  }

  generateId() {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  // Export functionality (keeping existing implementation)
  async exportToPDF(guide) {
    console.log('Exporting to PDF:', guide.title);
    return { success: true, message: 'PDF export not implemented yet' };
  }

  async exportToWord(guide) {
    console.log('Exporting to Word:', guide.title);
    return { success: true, message: 'Word export not implemented yet' };
  }
}

export default new StorageService();